/** A node that can be added to the flowchart */
export interface GraphBuilderNodeTemplate {
  clientId: string;
}

/** A node that has been added to a flowchart */
export type GraphBuilderNode = GraphBuilderNodeTemplate;

/** An edge that can be drawn in the flowchart */
export interface GraphBuilderEdgeTemplate {
  clientId: string;
  fromNodeId: string;
  toNodeId: string;
}

/** An edge that has been added to a flowchart */
export type GraphBuilderEdge = GraphBuilderEdgeTemplate;

/**
 *  Flowchart Design Space (i.e., the limitations within which a flowchart design is valid)
 */
export interface FlowchartDesignSpace<
  TNodeTemplate extends GraphBuilderNodeTemplate,
  TEdgeTemplate extends GraphBuilderEdgeTemplate = GraphBuilderEdgeTemplate,
  TEdge extends GraphBuilderEdge = GraphBuilderEdge,
> {
  /** If the design space of the flowchart is limited to specific nodes, provide them here */
  availableNodes?: Array<TNodeTemplate>;
  /** If the design space of the flowchart is limited to specific edges, provide them here */
  availableEdges?: Array<TEdgeTemplate>;
  /** If edge validation is dynamic, you can validate it here */
  validateEdge?: (edge: TEdge) => true | GraphBuilderValidationError;
}

/**
 * Error reporting - keep in sync with benchling/processes/api/utils/flowchart_validation/constants.py
 */

export enum GraphBuilderObjectType {
  NODE = 'NODE',
  EDGE = 'EDGE',
  UNKNOWN = 'UNKNOWN',
}

export enum GraphBuilderErrorType {
  NODE_NOT_AVAILABLE = 'NODE_NOT_AVAILABLE',
  NODE_MISSING_REQUIRED_PATH = 'NODE_MISSING_REQUIRED_PATH',
  NODE_NOT_CONNECTED = 'NODE_NOT_CONNECTED',
  TASK_NODE_NOT_CONNECTED = 'TASK_NODE_NOT_CONNECTED',
  OUTPUT_NODE_NOT_CONNECTED = 'OUTPUT_NODE_NOT_CONNECTED',
  ROOT_NODE_NOT_CONNECTED = 'ROOT_NODE_NOT_CONNECTED',
  ROUTER_NOT_CONNECTED = 'ROUTER_NOT_CONNECTED',
  END_NODE_NOT_CONNECTED = 'END_NODE_NOT_CONNECTED',
  END_NODE_CONNECTED_TO_NON_ROUTER_NODE = 'END_NODE_CONNECTED_TO_NON_ROUTER_NODE',
  END_NODE_CONNECTED_TO_MULTIPLE_SOURCE_NODES = 'END_NODE_CONNECTED_TO_MULTIPLE_SOURCE_NODES',
  END_NODE_HAS_OUTBOUND_EDGES = 'END_NODE_HAS_OUTBOUND_EDGES',
  NO_FLOWCHART_TASK_NODE = 'NO_FLOWCHART_TASK_NODE',
  NO_INTERMEDIATE_NODES = 'NO_INTERMEDIATE_NODES',
  TOO_MANY_START_NODES = 'TOO_MANY_START_NODES',
  NO_OUTPUT_NODE = 'NO_OUTPUT_NODE',
  EDGE_CONNECTS_UNAVAILABLE_NODES = 'EDGE_CONNECTS_UNAVAILABLE_NODES',
  INVALID_EDGE_BETWEEN_SCHEMAS = 'INVALID_EDGE_BETWEEN_SCHEMAS',
  EDGE_NOT_AVAILABLE = 'EDGE_NOT_AVAILABLE',
  DUPLICATE_EDGES = 'DUPLICATE_EDGES',
  EDGE_IS_PART_OF_LOOP = 'EDGE_IS_PART_OF_LOOP',
  ROUTER_HAS_MULTIPLE_SOURCE_NODES = 'ROUTER_HAS_MULTIPLE_SOURCE_NODES',
  ROUTER_MISSING_OUTGOING_EDGES = 'ROUTER_MISSING_OUTGOING_EDGES',
  ROUTER_CONNECTS_TO_ANOTHER_ROUTER = 'ROUTER_CONNECTS_TO_ANOTHER_ROUTER',
  ROUTER_ONLY_CONNECTED_TO_END_NODES = 'ROUTER_ONLY_CONNECTED_TO_END_NODES',
  CHAINED_ROUTER_LIMIT_EXCEEDED = 'CHAINED_ROUTER_LIMIT_EXCEEDED',
  CHAINED_ROUTERS_NOT_SUPPORTED = 'CHAINED_ROUTERS_NOT_SUPPORTED',
  ROUTER_EDGES_NOT_CONFIGURED = 'ROUTER_EDGES_NOT_CONFIGURED',
  DUPLICATE_PATHS_WITH_ROUTER = 'DUPLICATE_PATHS_WITH_ROUTER',
  MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT = 'MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT',
  ROUTER_HAS_MULTIPLE_PATHS_TO_SAME_SCHEMA_NODES = 'ROUTER_HAS_MULTIPLE_PATHS_TO_SAME_SCHEMA_NODES',
  MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCE_TYPES = 'MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCE_TYPES',
  MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCES_THAN_ROUTER_RULES = 'MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCES_THAN_ROUTER_RULES',
  LOOP_MISSING_TASK_OR_ROUTER_NODE = 'LOOP_MISSING_TASK_OR_ROUTER_NODE',
  LOOP_HAS_MULTIPLE_ENTRY_POINTS = 'LOOP_HAS_MULTIPLE_ENTRY_POINTS',
  MAX_NODES_LIMIT_EXCEEDED = 'MAX_NODES_LIMIT_EXCEEDED',
  MAX_EDGES_LIMIT_EXCEEDED = 'MAX_EDGES_LIMIT_EXCEEDED',
  UNKNOWN = 'UNKNOWN',
}

export interface GraphBuilderValidationError {
  objectId: string | null; // Null indicates a systemic issue (e.g., a missing node)
  objectType: GraphBuilderObjectType;
  errorMessage: string;
  errorType: GraphBuilderErrorType;
}

export type MaybeValidationError = GraphBuilderValidationError | null;

export interface GraphBuilderValidationResult {
  errors: Array<GraphBuilderValidationError>;
}

/**
 * Validation interface
 */
export interface FlowchartForValidation {
  nodes: Array<GraphBuilderNode>;
  edges: Array<GraphBuilderEdge>;
}

export type GraphBuilderValidator<
  TFlowchart extends FlowchartForValidation,
  TNodeTemplate extends GraphBuilderNodeTemplate,
  TEdgeTemplate extends GraphBuilderEdgeTemplate,
  TFlowchartDesignSpace extends FlowchartDesignSpace<TNodeTemplate, TEdgeTemplate>,
> = (flowchart: TFlowchart, designSpace: TFlowchartDesignSpace) => GraphBuilderValidationResult;
